import React, { useMemo } from 'react';
import { PetType } from '@spike/model';
import Button from 'components/UI/V3/Button/Button';
import { ServiceNameSection } from './Sections/ServiceName/ServiceNameSection';
import { DescriptionSection } from './Sections/Description/DescriptionSection';
import { TimesSection } from './Sections/Times/TimesSection';
import { ColorCodeSection } from './Sections/ColorCode/ColorCodeSection';
import { RoomsAndSpacesSection } from './Sections/RoomsAndSpaces/RoomsAndSpacesSection';
import { OnlineBookingSection } from './Sections/OnlineBooking/OnlineBookingSection';
import { ServiceFormsSection } from 'components/Service/Forms/ServiceFormsSection';
import { AssociatedForm } from 'model/Forms';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { OrganizationMarketplaceServicesInfo } from '@spike/marketplace-model';
import { DAYCARE_DEFAULTS, Service } from 'model/Service';
import { filterCompatibleRoomIds, flattenDaycareRoomUnits } from './Sections/RoomsAndSpaces/roomElements';
import styles from './DaycareDetailsStep.module.scss';

interface DaycareFormData {
    name: string;
    description?: string;
    dropOffTime: { from: string; to: string };
    pickUpTime: { from: string; to: string };
    color?: string;
    onlineBooking: boolean;
    forms?: AssociatedForm[];
    roomsEnabled?: boolean;
    selectedRooms?: Array<string>;
}

interface DaycareDetailsStepProps {
    formData: DaycareFormData;
    petTypes: Array<PetType>;
    marketplaces: Service['marketplaces'];
    serviceId?: number;
    onNext: (details: {
        name: string;
        description: string;
        dropOffTime: { from: string; to: string };
        pickUpTime: { from: string; to: string };
        color?: string;
        onlineBooking: boolean;
        forms?: AssociatedForm[];
        roomsEnabled?: boolean;
        selectedRooms?: Array<string>;
        marketplaces: Service['marketplaces'];
    }) => void;
}

const getRoomUnitIds = (marketplace: Service['marketplaces'][number]): Array<number> => {
    if ((marketplace.roomUnitIds ?? []).length > 0) {
        return marketplace.roomUnitIds;
    }

    // Legacy daycare services stored unit IDs in roomIds / room_ids.
    return marketplace.roomIds ?? [];
};

const withRoomUnitIds = (
    marketplaces: Service['marketplaces'],
    selectedRooms: Array<string>,
    organizationServicesInfo: Array<OrganizationMarketplaceServicesInfo>
): Service['marketplaces'] => {
    const hasAnyRoomUnitIds = marketplaces.some(marketplace => getRoomUnitIds(marketplace).length > 0);

    return marketplaces.map(marketplace => {
        const existingRoomUnitIds = getRoomUnitIds(marketplace);

        if (hasAnyRoomUnitIds || selectedRooms.length === 0) {
            return {
                ...marketplace,
                roomUnitIds: existingRoomUnitIds,
                roomIds: []
            };
        }

        const daycareRooms =
            organizationServicesInfo.find(info => info.marketplace.id === marketplace.marketplaceId)?.rooms?.daycare ??
            [];
        const unitIds = new Set(flattenDaycareRoomUnits(daycareRooms).map(unit => unit.unitId));

        return {
            ...marketplace,
            roomUnitIds: selectedRooms.map(roomId => Number(roomId)).filter(roomId => unitIds.has(roomId)),
            roomIds: []
        };
    });
};

const sameRoomUnitIds = (left: Array<number> = [], right: Array<number> = []) =>
    left.length === right.length && left.every((roomId, index) => roomId === right[index]);

const sameMarketplaces = (left: Service['marketplaces'], right: Service['marketplaces']) =>
    left.length === right.length &&
    left.every(
        (marketplace, index) =>
            marketplace.marketplaceId === right[index].marketplaceId &&
            sameRoomUnitIds(marketplace.roomUnitIds, right[index].roomUnitIds)
    );

export const DaycareDetailsStep: React.FC<DaycareDetailsStepProps> = props => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const selectedPetTypeIds = useMemo(() => props.petTypes.map(petType => petType.id), [props.petTypes]);
    const selectedRoomsFromForm = props.formData.selectedRooms ?? [];
    const marketplaceIdsKey = props.marketplaces.map(marketplace => marketplace.marketplaceId).join(',');
    const selectedRoomsKey = selectedRoomsFromForm.join(',');
    const selectedPetTypeIdsKey = selectedPetTypeIds.join(',');

    const [serviceName, setServiceName] = React.useState({
        value: props.formData.name || '',
        error: ''
    });
    const [description, setDescription] = React.useState({
        value: props.formData.description || '',
        error: ''
    });

    const [dropOffTime, setDropOffTime] = React.useState({
        from: props.formData.dropOffTime?.from || DAYCARE_DEFAULTS.DROP_OFF_TIME.from,
        to: props.formData.dropOffTime?.to || DAYCARE_DEFAULTS.DROP_OFF_TIME.to
    });
    const [pickUpTime, setPickUpTime] = React.useState({
        from: props.formData.pickUpTime?.from || DAYCARE_DEFAULTS.PICK_UP_TIME.from,
        to: props.formData.pickUpTime?.to || DAYCARE_DEFAULTS.PICK_UP_TIME.to
    });

    const [colorCode, setColorCode] = React.useState<string | null>(props.formData.color || null);
    const [onlineBooking, setOnlineBooking] = React.useState(props.formData.onlineBooking || false);
    const [forms, setForms] = React.useState<AssociatedForm[]>(props.formData.forms || []);
    const [roomsEnabled, setRoomsEnabled] = React.useState(props.formData.roomsEnabled || false);
    const [marketplaces, setMarketplaces] = React.useState<Service['marketplaces']>(() =>
        withRoomUnitIds(props.marketplaces, selectedRoomsFromForm, organizationServicesInfo)
    );

    React.useEffect(() => {
        setMarketplaces(prev => {
            const synced = withRoomUnitIds(props.marketplaces, selectedRoomsFromForm, organizationServicesInfo).map(
                marketplace => {
                    const existing = prev.find(item => item.marketplaceId === marketplace.marketplaceId);
                    return {
                        ...marketplace,
                        roomUnitIds: existing?.roomUnitIds ?? marketplace.roomUnitIds ?? [],
                        roomIds: []
                    };
                }
            );

            return sameMarketplaces(prev, synced) ? prev : synced;
        });
        // organizationServicesInfo is intentionally omitted: the hook returns a new array every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketplaceIdsKey, selectedRoomsKey]);

    React.useEffect(() => {
        if (!roomsEnabled) {
            return;
        }

        setMarketplaces(prev => {
            let changed = false;
            const next = prev.map(marketplace => {
                const daycareRooms =
                    organizationServicesInfo.find(info => info.marketplace.id === marketplace.marketplaceId)?.rooms
                        ?.daycare ?? [];
                const filteredRoomUnitIds = filterCompatibleRoomIds(
                    daycareRooms,
                    marketplace.roomUnitIds ?? [],
                    selectedPetTypeIds
                );

                if (sameRoomUnitIds(filteredRoomUnitIds, marketplace.roomUnitIds)) {
                    return marketplace;
                }

                changed = true;
                return { ...marketplace, roomUnitIds: filteredRoomUnitIds, roomIds: [] };
            });

            return changed ? next : prev;
        });
        // organizationServicesInfo is intentionally omitted: the hook returns a new array every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomsEnabled, selectedPetTypeIdsKey]);

    const handleServiceNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setServiceName({
            value: e.target.value,
            error: e.target.value.length === 0 ? 'Service name is required' : ''
        });
    }, []);

    const handleDescriptionChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription({
            value: e.target.value,
            error: e.target.value.length > 400 ? 'Description must be less than 400 characters' : ''
        });
    }, []);

    const handleDropOffTimeChange = React.useCallback((type: 'from' | 'to', time: string) => {
        setDropOffTime(prev => ({ ...prev, [type]: time }));
    }, []);

    const handlePickUpTimeChange = React.useCallback((type: 'from' | 'to', time: string) => {
        setPickUpTime(prev => ({ ...prev, [type]: time }));
    }, []);

    const handleRoomsEnabledChange = React.useCallback((enabled: boolean) => {
        setRoomsEnabled(enabled);
        if (!enabled) {
            setMarketplaces(prev => prev.map(marketplace => ({ ...marketplace, roomUnitIds: [], roomIds: [] })));
            setOnlineBooking(false);
        }
    }, []);

    const selectedRooms = useMemo(
        () =>
            Array.from(
                new Set(
                    marketplaces.flatMap(marketplace =>
                        (marketplace.roomUnitIds ?? []).map(roomUnitId => String(roomUnitId))
                    )
                )
            ),
        [marketplaces]
    );

    const isValid = React.useMemo(() => {
        const isNameValid = serviceName.error.length === 0 && serviceName.value.length > 0;
        const isDropOffValid = dropOffTime.from && dropOffTime.to;
        const isPickUpValid = pickUpTime.from && pickUpTime.to;
        const isRoomsValid = !roomsEnabled || selectedRooms.length > 0;

        return isNameValid && isDropOffValid && isPickUpValid && isRoomsValid;
    }, [serviceName, dropOffTime, pickUpTime, roomsEnabled, selectedRooms]);

    const onOnlineBookingChange = React.useCallback((checked: boolean) => {
        setOnlineBooking(checked);
        if (checked) {
            setRoomsEnabled(true);
        }
    }, []);

    const handleNext = React.useCallback(() => {
        if (isValid) {
            props.onNext({
                name: serviceName.value,
                description: description.value,
                dropOffTime,
                pickUpTime,
                color: colorCode || undefined,
                onlineBooking,
                forms,
                roomsEnabled,
                selectedRooms,
                marketplaces
            });
        }
    }, [
        serviceName.value,
        description.value,
        dropOffTime,
        pickUpTime,
        colorCode,
        onlineBooking,
        forms,
        roomsEnabled,
        selectedRooms,
        marketplaces,
        isValid,
        props
    ]);

    return (
        <div className={styles.container}>
            <ServiceNameSection
                value={serviceName.value}
                error={serviceName.error}
                onChange={handleServiceNameChange}
                className={styles.field}
            />

            <DescriptionSection
                value={description.value}
                error={description.error}
                onChange={handleDescriptionChange}
                className={styles.field}
            />

            <TimesSection
                dropOffTime={dropOffTime}
                pickUpTime={pickUpTime}
                onDropOffTimeChange={handleDropOffTimeChange}
                onPickUpTimeChange={handlePickUpTimeChange}
                className={styles.field}
            />

            <ColorCodeSection
                color={colorCode}
                onChange={setColorCode}
                className={styles.field}
            />

            <OnlineBookingSection
                checked={onlineBooking}
                onChange={onOnlineBookingChange}
                className={styles.field}
            />
            <ServiceFormsSection
                forms={forms}
                serviceId={props.serviceId}
                serviceCategory="daycare"
                onChange={setForms}
            />

            <RoomsAndSpacesSection
                enabled={roomsEnabled}
                marketplaces={marketplaces}
                selectedPetTypeIds={selectedPetTypeIds}
                onEnabledChange={handleRoomsEnabledChange}
                onChangeMarketplaces={setMarketplaces}
                className={styles.field}
            />

            <div className={styles.buttonContainer}>
                <Button
                    label="Next"
                    variant="primary"
                    size="large"
                    disabled={!isValid}
                    onClick={handleNext}
                />
            </div>
        </div>
    );
};
